import { useEffect, useRef } from "react";
import { AssistantErrorAlert } from "../../Assistant/AssistantErrorAlert";
import { IconX } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { Button, Spinner } from "@onengine/component-library";
import { useShallow } from "zustand/react/shallow";
import {
  useAssignmentsAssistantStore,
  type AssignmentsAssistantEntry,
  type AssignmentsTurnContext,
} from "@/store/form-builder/useAssignmentsAssistantStore";
import { useAssistantHandoffStore } from "@/store/form-builder/useAssistantHandoffStore";
import { AssistantComposer } from "@/components/features/FormBuilder/Assistant/AssistantComposer";
import { resolveFormTypeIds } from "@/components/features/FormBuilder/utils/resolveFormTypeIds";
import { ProposedChangesCard } from "./ProposedChangesCard";
import { NewFormCard } from "./NewFormCard";
import { CompanyPicker } from "./CompanyPicker";
import { slotLabelOf } from "./slotLabel";
import { slotPageSearch } from "./slotPage";
import { useApplyAssignmentChanges } from "./useApplyAssignmentChanges";
import panelStyles from "@/components/features/FormBuilder/Assistant/Assistant.module.scss";
import styles from "./AssignmentsAssistant.module.scss";

type NewFormEntry = Extract<AssignmentsAssistantEntry, { kind: "newForm" }>;
type FormChangeEntry = Extract<AssignmentsAssistantEntry, { kind: "formChange" }>;

// The person's own request, to show in the form builder chat: the latest thing
// they typed, skipping answers they picked from the assistant's choice buttons.
function lastRequest(messages: AssignmentsAssistantEntry[]): string | undefined {
  let typed: string | undefined;
  let offered: string[] = [];
  for (const entry of messages) {
    if (entry.kind === "assistant") offered = entry.choices;
    if (entry.kind === "user" && !offered.includes(entry.text)) typed = entry.text;
  }
  return typed;
}

interface AssignmentsAssistantPanelProps {
  tenantId?: string;
  focus?: AssignmentsTurnContext["focus"];
  /** What the assistant reads, as the page names it, e.g. "7-Eleven · Reactive · Assessment". */
  scopeLabel?: string;
  /**
   * Set on the all-companies view: with no company picked, questions are about
   * the whole platform, and one naming a company picks it.
   */
  companyPicker?: {
    value?: string;
    onChange: (tenantId: string | undefined) => void;
    known?: { id: string; name: string };
  };
}

export function AssignmentsAssistantPanel({
  tenantId: pageTenantId,
  focus,
  scopeLabel,
  companyPicker,
}: AssignmentsAssistantPanelProps) {
  // Questions go to the company picked, or to the whole platform with none.
  // Cards act for the company the conversation is about, which an answer may
  // have just picked before the picker catches up.
  const conversationTenantId = useAssignmentsAssistantStore((s) => s.tenantId);
  const tenantId = pageTenantId ?? (companyPicker ? conversationTenantId : undefined);
  const {
    messages,
    isBusy,
    send,
    approveChanges,
    cancelChanges,
    setNewFormStatus,
    setFormChangeStatus,
    addError,
    stop,
    reset,
    setOpen,
  } = useAssignmentsAssistantStore(
    useShallow((s) => ({
      messages: s.messages,
      isBusy: s.isBusy,
      send: s.send,
      approveChanges: s.approveChanges,
      cancelChanges: s.cancelChanges,
      setNewFormStatus: s.setNewFormStatus,
      setFormChangeStatus: s.setFormChangeStatus,
      addError: s.addError,
      stop: s.stop,
      reset: s.reset,
      setOpen: s.setOpen,
    })),
  );
  const { apply, applyAll, skip } = useApplyAssignmentChanges(tenantId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const router = useRouter();
  const listEndRef = useRef<HTMLDivElement>(null);
  const askingTenantId = pageTenantId ?? companyPicker?.value;
  const canAsk = !!askingTenantId || !!companyPicker;
  const ask = (text: string) => void send(text, { tenantId: askingTenantId, focus });
  const lastIndexOf = (kind: AssignmentsAssistantEntry["kind"]) =>
    messages.map((m) => m.kind).lastIndexOf(kind);
  const lastUserIndex = lastIndexOf("user");
  const lastAssistantIndex = lastIndexOf("assistant");
  const lastEntry = messages.at(-1);
  const isPlanningForm = lastEntry?.kind === "newForm" && lastEntry.status === "planning";

  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages.length, isBusy]);

  const openInFormBuilder = async (entry: NewFormEntry) => {
    if (!tenantId || !entry.plan || entry.status !== "pending") return;
    const { request, plan } = entry;
    // Marked first, so a second click while the ids load does nothing.
    setNewFormStatus(entry.id, "approved");
    const ids = await resolveFormTypeIds(
      queryClient,
      request.workOrderType,
      request.formType,
    ).catch(() => null);
    if (!ids?.formTypeId || !ids.workOrderTypeId) {
      setNewFormStatus(entry.id, "failed");
      addError(
        "Could not find this visit type or category in the form builder. Nothing was opened. Try again, or create the form from Form Templates.",
      );
      return;
    }
    // The page's client, brand and trade only carry over when the form is for the
    // category on screen; otherwise it goes to the company default.
    const sameCategory = focus?.workOrderType === request.workOrderType;
    const scope = sameCategory
      ? {
          clientId: focus?.clientId,
          brandId: focus?.brandId,
          tradeId: focus?.tradeId,
          subtradeId: focus?.subtradeId,
        }
      : {};
    useAssistantHandoffStore.getState().start({
      build: { brief: `${request.name}. ${request.brief}`, plan, said: lastRequest(messages) },
      assign: {
        tenantId,
        workOrderType: request.workOrderType,
        formType: request.formType,
        slotLabel: slotLabelOf(request.workOrderType, request.formType),
        scope,
        // Back to the page that shows this slot's form selector, wherever the
        // person asked from, with the slot highlighted.
        returnTo: {
          href: router.buildLocation({
            to: "/form-builder/assignments/$category",
            params: { category: request.workOrderType },
            search: slotPageSearch(
              { tenantId, workOrderType: request.workOrderType, formType: request.formType, scope },
              { pathname: location.pathname, search: location.search as Record<string, unknown> },
            ),
          }).href,
        },
      },
    });
    navigate({
      to: "/form-builder/editor",
      search: {
        formName: request.name,
        tenantId,
        formTypeId: ids.formTypeId,
        workOrderTypeId: ids.workOrderTypeId,
        ...(scope.tradeId ? { tradeIds: String(scope.tradeId) } : {}),
        ...(scope.subtradeId ? { subTradeIds: String(scope.subtradeId) } : {}),
      },
    });
  };

  // The form opens in the form builder with the change proposed in the preview.
  // Nothing is saved: the person applies it and publishes it, or discards it.
  const openForChange = (entry: FormChangeEntry) => {
    if (!tenantId || entry.status !== "pending") return;
    const { request } = entry;
    setFormChangeStatus(entry.id, "opened");
    useAssistantHandoffStore.getState().startChange({
      build: { formId: request.templateId, brief: request.brief, said: lastRequest(messages) },
      back: {
        formId: request.templateId,
        formName: request.formName,
        tenantId,
        returnTo: { href: location.href },
      },
    });
    navigate({ to: "/form-builder/editor", search: { formId: request.templateId, tenantId } });
  };

  // The person asked for the change and confirmed it here, so the form opens
  // right away; the card's button is there if it did not.
  // A change restored by a reload waits for the button instead of navigating away.
  const latest = messages.at(-1);
  const shownOnMount = useRef(latest?.id);
  useEffect(() => {
    if (latest?.id === shownOnMount.current) return;
    if (latest?.kind === "formChange" && latest.status === "pending") openForChange(latest);
  }, [latest]);

  return (
    <aside className={panelStyles.panel} aria-label="Assignments assistant">
      <header className={panelStyles.panelHeader}>
        <div className={panelStyles.panelTitle}>
          <span className={panelStyles.cardTitle}>Assistant</span>
          <span className={panelStyles.cardMeta}>
            Reads assignments and proposes changes. You apply them.
          </span>
        </div>
        <div className={panelStyles.panelHeaderActions}>
          <Button variant="tertiary" size="small" onClick={reset}>
            New chat
          </Button>
          <Button
            type="icon"
            variant="tertiary"
            size="small"
            iconLeft={<IconX size={16} />}
            onClick={() => setOpen(false)}
            htmlAttributes={{ "aria-label": "Close assistant" }}
          />
        </div>
      </header>

      {companyPicker ? (
        <>
          <CompanyPicker
            value={companyPicker.value}
            onChange={companyPicker.onChange}
            known={companyPicker.known}
            disabled={isBusy}
          />
          {!companyPicker.value && (
            <p className={styles.scopeLine}>
              Reading the <strong>ServEngine defaults</strong>. Name a company to ask about its own.
            </p>
          )}
        </>
      ) : (
        scopeLabel && (
          <p className={styles.scopeLine}>
            Reading <strong>{scopeLabel}</strong>
          </p>
        )
      )}

      <div className={panelStyles.messages} aria-live="polite">
        <p className={panelStyles.assistantBubble}>
          Ask which form a visit gets, what to change, or for a new form. I check the assignments
          first, so nothing changes by surprise.
        </p>
        {messages.map((entry, index) => {
          switch (entry.kind) {
            case "user":
              return (
                <p key={entry.id} className={panelStyles.userBubble}>
                  {entry.text}
                </p>
              );
            case "assistant": {
              const canPick =
                index === lastAssistantIndex && index > lastUserIndex && !isBusy && canAsk;
              return (
                <div key={entry.id} className={styles.assistantTurn}>
                  <p className={panelStyles.assistantBubble}>{entry.text}</p>
                  {canPick && entry.choices.length > 0 && (
                    <div className={styles.choices} role="group" aria-label="Answers">
                      {entry.choices.map((choice) => (
                        <Button
                          key={choice}
                          variant="secondary"
                          size="small"
                          onClick={() => ask(choice)}
                        >
                          {choice}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            case "note":
              return (
                <p key={entry.id} className={panelStyles.note}>
                  {entry.text}
                </p>
              );
            case "error":
              return <AssistantErrorAlert key={entry.id} text={entry.text} reference={entry.ref} />;
            case "usedRows":
              return (
                <section key={entry.id} aria-label="Assignments used for the answer">
                  <span className={panelStyles.cardMeta}>Based on these assignments:</span>
                  <ul className={styles.usedRows}>
                    {entry.rows.map((row) => (
                      <li key={row.id}>
                        {row.where}: <strong>{row.templateName}</strong>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            case "changes":
              return (
                <ProposedChangesCard
                  key={entry.id}
                  approval={entry.approval}
                  changes={entry.changes}
                  isBusy={isBusy}
                  onApprove={() => approveChanges(entry.id)}
                  onCancel={() => cancelChanges(entry.id)}
                  onApply={(change) => void apply(entry.id, change)}
                  onSkip={(change) => skip(entry.id, change)}
                  onApplyAll={() =>
                    void applyAll(entry.changes.map((change) => ({ entryId: entry.id, change })))
                  }
                />
              );
            case "newForm":
              return (
                <NewFormCard
                  key={entry.id}
                  request={entry.request}
                  plan={entry.plan}
                  status={entry.status}
                  isBusy={isBusy}
                  onApprove={() => void openInFormBuilder(entry)}
                  onChange={() => {
                    setNewFormStatus(entry.id, "cancelled");
                    ask(`Change the plan for "${entry.request.name}".`);
                  }}
                  onCancel={() => setNewFormStatus(entry.id, "cancelled")}
                />
              );
            case "formChange":
              return (
                <section
                  key={entry.id}
                  className={panelStyles.proposalCard}
                  aria-label="Change to a form"
                >
                  <span className={panelStyles.cardTitle}>{entry.request.formName}</span>
                  <span className={panelStyles.cardMeta}>{entry.request.brief}</span>
                  {entry.status === "pending" && (
                    <div className={panelStyles.cardActions}>
                      <Button variant="primary" size="small" onClick={() => openForChange(entry)}>
                        Open in form builder
                      </Button>
                    </div>
                  )}
                  <span className={panelStyles.cardMeta}>
                    {entry.status === "cancelled"
                      ? "Not opened."
                      : "The form builder shows this change in the preview. Nothing is saved until you apply it and publish."}
                  </span>
                </section>
              );
          }
        })}
        {isBusy && !isPlanningForm && (
          <p className={panelStyles.busy}>
            <Spinner size="small" />
            Reading the assignments
          </p>
        )}
        <div ref={listEndRef} />
      </div>

      <AssistantComposer
        isBusy={isBusy}
        placeholder={
          askingTenantId
            ? "Ask about, change or create a form"
            : "e.g. Which assessment does a reactive HVAC visit get?"
        }
        hint="Nothing changes until you approve and apply."
        disabledReason={canAsk ? undefined : "Open a company's assignments to use the assistant."}
        onSend={ask}
        onStop={stop}
      />
    </aside>
  );
}
